import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

export const taxCompliantAccountDetail = createAsyncThunk(
  "taxCompliantAccount/detail",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("Invalid tax compliant account id");

      const response = await axiosInstance.get(
        `${baseUrl}/accounts/tds/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      const data = response?.data;

      if (!data?.status) {
        return rejectWithValue("Failed to fetch account details");
      }

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.errors?.base?.[0] ||
        error?.response?.data?.message ||
        error.message;
      CommonToast({
        text: message,
        icon: ICONS?.INFO_FILLED,
        variant: "var(--error)",
        buttonNeeded: false,
      });

      return rejectWithValue(message);
    }
  },
);

export const achivedTaxAccountDetail = createAsyncThunk(
  "achivedTaxAccountDetail",
  async ({ id, Navigate }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("q2p_token");

      const response = await axiosInstance.put(
        `${baseUrl}/accounts/tds/${id}/archive`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": token,
          },
        },
      );

      return response.data;
    } catch (error) {
      if (error?.response?.data?.errors?.user_unauthorized) {
        Navigate("/feature_not_assigned", {
          state: {
            unavailableService: "ArchiveTDSAccount",
          },
        });
        return;
      }

      if (error?.response?.data?.errors?.tds_account) {
        let errorMessage = error?.response?.data?.errors?.tds_account?.[0];
        CommonToast({
          text: errorMessage,
          icon: ICONS?.INFO_FILLED,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      } else if (
        error?.response?.data?.errors?.bank_account_number ||
        error?.response?.data?.errors?.permanent_account_number
      ) {
        let errorMessage =
          error?.response?.data?.errors?.bank_account_number[0] ||
          error?.response?.data?.errors?.permanent_account_number[0];
        CommonToast({
          text: errorMessage,
          icon: ICONS?.INFO_FILLED,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      let errorMessage =
        error?.response?.data?.errors?.bank_account_number[0] ||
        error?.response?.data?.errors?.tds_account?.[0] ||
        error?.response?.data?.errors?.permanent_account_number[0] ||
        error?.response?.data?.errors[0]?.gstin_detail?.base ||
        error?.response?.data?.errors?.base[0];

      CommonToast({
        text: errorMessage,
        icon: ICONS?.INFO_FILLED,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);
export const unachivedTaxAccountDetail = createAsyncThunk(
  "unachivedTaxAccountDetail",
  async ({ id, Navigate }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("q2p_token");

      const response = await axiosInstance.put(
        `${baseUrl}/accounts/tds/${id}/unarchive`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": token,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error?.response?.data?.errors?.user_unauthorized) {
        Navigate("/feature_not_assigned", {
          state: {
            unavailableService: "ArchiveTDSAccount",
          },
        });
        return;
      }
      const errData = error?.response?.data?.errors;

      if (!errData.base) {
        errData.tds_account.forEach((error3) => {
          if (error?.response?.data.status === false) {
            setTimeout(() => {
              CommonToast({
                text: error3,
                icon: ICONS?.INFO_FILLED,
                variant: "var(--error)",
                buttonNeeded: false,
              });
            }, 1000);
          }
        });
      } else {
        CommonToast({
          text: errData.base[0],
          icon: ICONS?.INFO_FILLED,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }

      return rejectWithValue(errData);
    }
  },
);

export const tdsWaitingApproved = createAsyncThunk(
  "tdsWaitingApproved",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/tds/${id}/approve`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return rejectWithValue(undefined);
      }

      const errData = error?.response?.data || {
        status: false,
        message: "Something went wrong",
      };

      return rejectWithValue(errData);
    }
  },
);

export const tdsDeniedAccount = createAsyncThunk(
  "tdsDeniedAccount",
  async ({ id, AccountIdData }, { rejectWithValue }) => {
    try {
      const data = {
        rejection_reason: AccountIdData,
      };

      const response = await axiosInstance.put(
        `${baseUrl}/accounts/tds/${id}/reject`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return undefined;
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || null);
    }
  },
);

export const editDetailsTds = createAsyncThunk(
  "editDetailsTds",
  async (values, { rejectWithValue }) => {
    const {
      id,
      pan,
      contact_no,
      email,
      gstin,
      category,
      name,
      navigate,
      recipient_email,
      reimbursement_user_id,
    } = values;

    const requestOptions = {
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": sessionStorage.getItem("q2p_token"),
      },
    };

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/accounts/tds/${id}/edit_other_details`,
        {
          tds_accounts: {
            id,
            pan,
            email,
            contact_no,
            gstin,
            category,
            name,
            recipient_email,
            reimbursement_user_id,
          },
        },
        requestOptions,
      );

      return response.data;
    } catch (error) {
      let errorMessage;

      if (error.response) {
        if (error.response.data?.errors?.user_unauthorized) {
          navigate("/feature_not_assigned", {
            state: { unavailableService: "CTDSAccount" },
          });
        }
        errorMessage =
          error.response.data?.errors?.bank_account_number?.[0] ||
          error.response.data?.errors?.tds_account?.[0] ||
          error.response.data?.errors?.permanent_account_number?.[0] ||
          error.response.data?.errors?.[0]?.gstin_detail?.base ||
          error.response.data?.errors?.base?.[0] ||
          error.response.data?.errors?.[0]?.gstin_detail?.base?.[0] ||
          (Array.isArray(error.response.data?.errors?.category)
            ? error.response.data?.errors?.category[0]
            : undefined) ||
          "Something went wrong";
      } else {
        errorMessage = error.message;
      }
      CommonToast({
        text: errorMessage,
        icon: ICONS?.INFO_FILLED,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(errorMessage);
    }
  },
);

const taxCompliantAccountDetailSlice = createSlice({
  name: "taxCompliantAccountDetail",
  initialState: {
    taxCompliantDetailsData: null,
    taxCompliantDetailsLoading: false,
    taxCompliantDetailsError: null,

    editDetailsTdsData: null,
    editDetailsTdsLoading: null,
    editDetailsTdsError: null,
  },
  reducers: {
    resetEditTds(state) {
      state.editTdsData = null;
    },
    resetTaxCompliantAccountDetail(state) {
      state.taxCompliantDetailsData = null;
      state.taxCompliantDetailsError = null;
      state.taxCompliantDetailsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(taxCompliantAccountDetail.pending, (state) => {
        state.taxCompliantDetailsLoading = true;
        state.taxCompliantDetailsError = null;
      })
      .addCase(taxCompliantAccountDetail.fulfilled, (state, action) => {
        state.taxCompliantDetailsLoading = false;
        state.taxCompliantDetailsData = action.payload;
      })
      .addCase(taxCompliantAccountDetail.rejected, (state, action) => {
        state.taxCompliantDetailsLoading = false;
        state.taxCompliantDetailsError = action.payload;
      });

    builder.addCase(unachivedTaxAccountDetail.pending, (state, action) => {
      state.unachivedLoadingDetail = true;
    });
    builder.addCase(unachivedTaxAccountDetail.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        CommonToast({
          text: `Account unarchived successfully`,
          icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
          variant: "var(--success)",
          buttonNeeded: false,
        });
      }
      state.unachivedLoadingDetail = false;
      state.unachivedDataDetail = action.payload;
    });
    builder.addCase(unachivedTaxAccountDetail.rejected, (state, action) => {
      state.unachivedErrorDetail = true;
    });
    builder.addCase(achivedTaxAccountDetail.pending, (state, action) => {
      state.achivedLoadingDetail = true;
    });
    builder.addCase(achivedTaxAccountDetail.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        CommonToast({
          text: `Account archived successfully`,
          icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
          variant: "var(--success)",
          buttonNeeded: false,
        });
      }
      state.achivedLoadingDetail = false;
      state.achivedDataDetail = action.payload;
    });
    builder.addCase(achivedTaxAccountDetail.rejected, (state, action) => {
      state.achivedErrorDetail = true;
    });

    builder.addCase(tdsWaitingApproved.pending, (state, action) => {
      state.tdsWaitingApprovalLoading = true;
    });
    builder.addCase(tdsWaitingApproved.fulfilled, (state, action) => {
      state.tdsWaitingApprovalLoading = false;
      state.tdsWaitingApprovalData = action.payload;
    });
    builder.addCase(tdsWaitingApproved.rejected, (state, action) => {
      state.tdsWaitingApprovalError = true;
      state.tdsWaitingApprovalData = null;
      state.tdsWaitingApprovalLoading = false;
    });

    builder.addCase(tdsDeniedAccount.pending, (state, action) => {
      state.tdsDeniedApprovalLoading = true;
    });
    builder.addCase(tdsDeniedAccount.fulfilled, (state, action) => {
      state.tdsDeniedApprovalLoading = false;
      state.tdsDeniedApprovalData = action.payload;
    });
    builder.addCase(tdsDeniedAccount.rejected, (state, action) => {
      state.tdsDeniedApprovalError = true;
      state.tdsDeniedApprovalData = null;
      state.tdsDeniedApprovalLoading = false;
    });

    builder.addCase(editDetailsTds.pending, (state) => {
      state.editDetailsTdsLoading = true;
    });
    builder.addCase(editDetailsTds.fulfilled, (state, action) => {
      state.editDetailsTdsLoading = false;
      state.editDetailsTdsData = action.payload;
    });
    builder.addCase(editDetailsTds.rejected, (state, action) => {
      state.editDetailsTdsError = true;
    });
  },
});

export const { resetTaxCompliantAccountDetail } =
  taxCompliantAccountDetailSlice.actions;

export default taxCompliantAccountDetailSlice.reducer;
